import { useCallback, useEffect, useState } from 'react';
import { identityManagementService } from '../services/imsService';
import { Optin, optinService } from '../services/optinService';
import { RegistrationFlowConfiguration } from './useRegistrationFlowConfiguration';
import { GetImsIdentityResponse } from '../types/ims';
import { mapImsIdentityToInitialFormValues } from '../utils/mapImsIdentityToInitialFormValues';
import { RegistrationFlowFormValues } from '../types/registrationFlows';

export const defaultInitialFormValues: RegistrationFlowFormValues = {
  firstName: undefined,
  lastName: undefined,
  birthDateDay: undefined,
  birthDateMonth: undefined,
  birthDateYear: undefined,
  gender: undefined,
  genderOther: undefined,
  address: {
    city: undefined,
    postalCode: undefined,
    street: undefined,
    houseNumber: undefined,
    box: undefined,
  },
  optins: [],
  telephone: undefined,
  country: undefined,
};

function promiseWithTimeout<T>(
  promise: Promise<T>,
  ms = 2000,
  timeoutError = new Error('Promise timed out')
): Promise<T> {
  // create a promise that rejects in milliseconds
  const timeout = new Promise<never>((resolve, reject) => {
    setTimeout(() => {
      reject(timeoutError);
    }, ms);
  });

  // returns a race between timeout and the passed promise
  return Promise.race<T>([promise, timeout]);
}

const isFulfilled = <T>(p: PromiseSettledResult<T>): p is PromiseFulfilledResult<T> =>
  p.status === 'fulfilled';

function buildInitialFormValues(
  defaultInitialFormValues: RegistrationFlowFormValues,
  countryISO: string | undefined,
  identity: GetImsIdentityResponse | undefined,
  optins: Optin[] | undefined
) {
  const initialRegistrationFormValues: RegistrationFlowFormValues = {
    ...defaultInitialFormValues,
    country: countryISO ?? defaultInitialFormValues.country,
    optins: optins ?? defaultInitialFormValues.optins,
  };
  return identity
    ? mapImsIdentityToInitialFormValues(identity, initialRegistrationFormValues)
    : initialRegistrationFormValues;
}

export function useInitialFormValues(
  identityId: string | undefined,
  countryISO: string | undefined,
  config: RegistrationFlowConfiguration
): {
  initialFormValues: RegistrationFlowFormValues | undefined;
} {
  const [initialFormValues, setInitialFormValues] = useState<RegistrationFlowFormValues>();

  const fetchInitialFormValues = useCallback(async () => {
    try {
      const identityFetch = identityId
        ? identityManagementService.getIdentity(identityId)
        : Promise.resolve(undefined);
      const optinsFetch = optinService.getOptinConfig({
        identity_level: config.identityLevel,
        action: config.action,
        brand: config.brand,
        context: config.process,
      });

      const externalCalls = [
        promiseWithTimeout(identityFetch),
        promiseWithTimeout(optinsFetch),
      ] as const;

      const [identityResult, optinsResult] = await Promise.allSettled(externalCalls);

      setInitialFormValues(
        buildInitialFormValues(
          defaultInitialFormValues,
          countryISO,
          isFulfilled(identityResult) ? identityResult.value : undefined,
          isFulfilled(optinsResult) ? optinsResult.value : undefined
        )
      );
    } catch (e: unknown) {
      setInitialFormValues(defaultInitialFormValues);
    }
  }, [countryISO, config.action, config.brand, config.identityLevel, config.process, identityId]);

  useEffect(() => {
    fetchInitialFormValues();
  }, [fetchInitialFormValues]);

  return { initialFormValues };
}
